import "./food-pref.scss";

import React, { useState, useEffect } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import {
  createGuestGroup,
  fromFirestoreDoc,
  toFirestoreGuestGroup,
} from "../../models/guestGroup";
import { db } from "../../firebase";

export default function FoodPreferences() {
  const EMAIL_ERROR_RESPONSE = `The email address entered is invalid or could not be found. \nPlease check and try again.`;

  const MEAL_PREF_LANDING_MESSAGE = `Please enter the email address you used to RSVP below`;

  const FOOD_PREF_1 = "Beef & Chicken Duo";
  const FOOD_PREF_2 = "Salmon";
  const FOOD_PREF_3 = "Vegetable Risotto";

  //data
  const [data, setData] = useState([]);
  const [isGuest, setIsGuest] = useState(false); //default user is not a guest
  const [guestGroup, setGuestGroup] = useState(createGuestGroup());
  const [isValidEmail, setIsValidEmail] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const [isFormSent, setIsFormSent] = useState(false);

  //fetch data
  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, "guestGroup"));
      const fetchedData = snapshot.docs.map(fromFirestoreDoc);
      setData(fetchedData);
    };
    fetchData();
  }, []);

  // Validate email before enabling verification button
  function handleValidation() {
    const emailInput = document.getElementById(
      "email-verification-input",
    ).value;
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput);
    setIsValidEmail(isValid);
  }

  // Find and load guest group by email
  function handleEmailVerification() {
    const email = document
      .getElementById("email-verification-input")
      .value.trim()
      .toLowerCase();

    const matchedGroup = data.find((g) => g.email.toLowerCase() === email);

    if (matchedGroup) {
      if (matchedGroup) {
        setGuestGroup({
          ...matchedGroup,
          guests: matchedGroup.guests.map((g) => ({
            ...g,
            isAttendingCeremony:
              g.isAttendingCeremony === false ? null : g.isAttendingCeremony,
            isAttendingReception:
              g.isAttendingReception === false ? null : g.isAttendingReception,
            mealPreference: g.mealPreference === "" ? null : g.mealPreference,
            dietaryRestrictions:
              g.dietaryRestrictions === "" ? null : g.dietaryRestrictions,
          })),
        });
        setIsGuest(true);
      }
    } else {
      setIsGuest(false);
      setErrorMessage(EMAIL_ERROR_RESPONSE);
    }
  }

  function handleFoodPrefChange(index, field, radioSelection) {
    const updatedPref = [...guestGroup.guests];
    updatedPref[index] = {
      ...updatedPref[index],
      [field]: radioSelection,
    };
    setGuestGroup((prev) => ({
      ...prev,
      guests: updatedPref,
    }));
    setIsFormValid(true);
    for (let guest of updatedPref) {
      if (guest.mealPreference === null) {
        setIsFormValid(false);
      }
    }
  }

  function handleDietaryRestrictions(index, field, value) {
    const updatedRestrictions = [...guestGroup.guests];
    if (value !== null && value.length > 255) {
      alert("Dietary restrictions cannot exceed 255 characters.");
      return;
    }
    updatedRestrictions[index] = {
      ...updatedRestrictions[index],
      [field]: value,
    };
    setGuestGroup((prev) => ({
      ...prev,
      guests: updatedRestrictions,
    }));
  }

  async function handleSubmit(e) {
    if (!canSubmitPref()) {
      alert("Please answer all questions before submitting.");
      return;
    }
    e.preventDefault();
    console.log("Attempting to submit food preferences");

    // Safety check: does guestGroup exist?
    if (!guestGroup) {
      console.error("guestGroup is undefined!");
      alert("Something went wrong: guest data is missing.");
      return;
    }

    // Safety check: does guestGroup have a valid id?
    if (!guestGroup.id) {
      console.error("guestGroup.id is missing!", guestGroup);
      alert("Something went wrong: Guest ID is missing.");
      return;
    }

    // Safety check: is guests array valid?
    if (!Array.isArray(guestGroup.guests) || guestGroup.guests.length === 0) {
      console.error(
        "guestGroup.guests is invalid or empty!",
        guestGroup.guests,
      );
      alert("Something went wrong: Guest list is empty.");
      return;
    }

    try {
      const guestRef = doc(db, "guestGroup", guestGroup.id);

      // Log what we are about to send to Firestore

      const updateData = {
        guests: guestGroup.guests,
        message: guestGroup.message || "",
        hasResponded: guestGroup.hasResponded || true,
        hasSubmittedMealPref: guestGroup.hasSubmittedMealPref || true, // Mark that meal preferences have been submitted
      };
      console.log("Updating Firestore document with:", updateData);

      await updateDoc(guestRef, updateData);

      console.log("food preferences successfully updated in Firestore.");

      // Update local state
      setGuestGroup((prev) => ({
        ...prev,
        hasResponded: true,
        hasSubmittedMealPref: true,
      }));

      setIsFormSent(true);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth", // Optional: adds a smooth scrolling animation
      });

      alert("food preferences submitted successfully! Thank you.");
    } catch (error) {
      console.error("Error submitting food preferences:", error);

      // Provide more informative message depending on error
      if (error.code === "permission-denied") {
        alert("Cannot submit food preferences: Firestore permissions denied.");
      } else if (error.code === "not-found") {
        alert("Cannot submit food preferences: Document not found.");
      } else {
        alert("Something went wrong. Check console for details.");
      }
    }
  }

  function canSubmitPref() {
    if (isFormValid) {
      return guestGroup.guests.every((guest) => guest.mealPreference !== null);
    } else {
      return false;
    }
  }

  return (
    <>
      <div className="prefs-container form-fade-in justify-center text-center padding-bottom-40">
        {!isGuest && !isFormSent && (
          <div className="padding-bottom-30">
            <>
              <h2>Meal Preferences</h2>
              <form
                className="verification-container"
                method="post"
                onSubmit={(e) => {
                  e.preventDefault(); //prevent page reload
                  handleEmailVerification();
                }}
              >
                <label
                  className="cormorant-light-500"
                  htmlFor="email-verification-input"
                >
                  {MEAL_PREF_LANDING_MESSAGE}
                </label>
                <input
                  id="email-verification-input"
                  type="email"
                  placeholder="your@email.com"
                  onKeyUp={handleValidation}
                />
                <button disabled={!isValidEmail} type="submit">
                  VALIDATE EMAIL
                </button>
                <p className="cormorant-light-500 error-message">
                  {" "}
                  {errorMessage.split("\n").map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </p>
              </form>
            </>
          </div>
        )}
        {/* guest hasn't responded */}
        {isGuest && !isFormSent && !guestGroup.hasSubmittedMealPref && (
          <div className="">
            <h2>Meal Preferences</h2>
            <p>Please submit your meal preferences by April 13, 2026</p>
            <form className="" onSubmit={handleSubmit} method="post">
              {guestGroup.guests.map((guest, index) => (
                <div key={index} className="">
                  <h4 className="margin-bottom-2">{guest.name}</h4>

                  <p className="margin-top-2 margin-bottom-1">
                    Please select one:
                  </p>
                  <div className="prefs-meal-selection padding-top-2 padding-bottom-2">
                    <input
                      type="radio"
                      id={`option-1-${index}`}
                      name={`mealPreference-${index}`}
                      value="1"
                      onChange={(e) =>
                        handleFoodPrefChange(
                          index,
                          "mealPreference",
                          FOOD_PREF_1,
                        )
                      }
                    />
                    <label htmlFor={`mealPreference-${index}`}>
                      {FOOD_PREF_1}
                    </label>
                    <input
                      type="radio"
                      id={`option-2-${index}`}
                      name={`mealPreference-${index}`}
                      value="2"
                      onChange={(e) =>
                        handleFoodPrefChange(
                          index,
                          "mealPreference",
                          FOOD_PREF_2,
                        )
                      }
                    />
                    <label htmlFor={`mealPreference-${index}`}>
                      {FOOD_PREF_2}
                    </label>
                    <input
                      type="radio"
                      id={`option-3-${index}`}
                      name={`mealPreference-${index}`}
                      value="3"
                      onChange={(e) =>
                        handleFoodPrefChange(
                          index,
                          "mealPreference",
                          FOOD_PREF_3,
                        )
                      }
                    />
                    <label htmlFor={`mealPreference-${index}`}>
                      {FOOD_PREF_3}
                    </label>
                  </div>
                  <p className="margin-top-2 margin-bottom-1">
                    Dietary Restrictions/Allergies:
                  </p>
                  <textarea
                    id={`dietary-restrictions-${index}`}
                    name={`dietary-restrictions-${index}`}
                    className="prefs-textarea margin-top-2"
                    placeholder="Please specify any dietary restrictions or allergies and we will do our best to accommodate"
                    maxLength="255"
                    value={""}
                    onChange={(e) =>
                      handleDietaryRestrictions(
                        index,
                        "dietaryRestrictions",
                        e.target.value ?? "n/a",
                      )
                    }
                  ></textarea>
                </div>
              ))}
              <div className="margin-top-10 margin-bottom-10">
                <button type="submit" disabled={!canSubmitPref()}>
                  SUBMIT MEAL PREFERENCES
                </button>
              </div>
            </form>
          </div>
        )}
        {/* form sent */}
        {isGuest && isFormSent && (
          <div className="padding-bottom-50">
            <a href="/details" className="font-size-5">
              Thank you for submitting your preferences!
              <br />
              You can check out the event details here.
            </a>
          </div>
        )}
        {/* has already submitted meal preferences */}
        {isGuest && !isFormSent && guestGroup.hasSubmittedMealPref && (
          <div className="padding-bottom-50 padding-left-6 padding-right-6">
            <p className="font-size-5 margin-bottom-5">
              We have already received your meal preferences. Thank you for
              submitting!
              <br />
              If you have any questions or require changes to your meal, please
              contact Ella or Chris directly.
            </p>
            <p className="font-size-5 margin-bottom-5">
              As a reminder, you can find everything you need to know for the
              big day including the timeline, venue details, attire, and hotel
              block information on{" "}
              <a href="/details" className="font-size-5 underline">
                our details page
              </a>
              .
            </p>
            <p className="font-size-5">
              Please be mindful that the hotel block will remain available until{" "}
              <strong className="underline">May 11, 2026</strong>.
            </p>
            <p className="font-size-5">We can't wait to celebrate with you!</p>
          </div>
        )}
      </div>
    </>
  );
}
